import config from 'config/config';
import { getStepIndex } from 'config/StepsOrder';
import { AnswersState } from 'schemas/answerStateSchema';
import { store } from 'store/store';
import { isRosselVersion } from 'utils/vendorUtils';
import { getNumPanels } from '../map/mapUtils';

// Declare gTM dataLayer array.
declare global {
  interface Window {
    dataLayer: Record<string, any>;
  }
}

// const taggingSteps = [
//   'FLOW_CHOICE', // step 0
//   'ADDRESS_CHOICE', // step 1
//   'ROOF_ANGLE', // step 2
//   'ROOF_TYPE', // step 3
//   'PANEL_SELECTION', // step 4
//   'MAP_EDITION', // step 5
//   'CONSUMPTION', // step 6
//   'NETWORK_TYPE', // step 7
//   'INVERTER_TYPE', // step 8
//   'BATTERY', // step 9
//   'EVEHICLE', // step 10
//   'SUMMARY', // step 11
//   'ESTIMATE', // step 12
// ];

const parseTaggingName = (routeName: string) => {
  const routeNameWithoutSlash = routeName.replace('/', '');
  return routeNameWithoutSlash.toUpperCase();
};

/**
 * Convert a bool to a "readable" value
 * Here implementation request is "oui" or "non"
 */
const convertBool = (val: boolean | undefined) => {
  if (val !== undefined) {
    return val ? 'Oui' : 'Non';
  }
  return val;
};

const isCustomRoofAngle = (roofAngle: number) =>
  !config.options.roofAngles.some(({ value }) => value === roofAngle);

const removeUndefined = (obj: Record<string, any>) => {
  const newObject: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) {
      newObject[key] = obj[key];
    }
  });
  return newObject;
};

const getNumPanelsWithExplicit = (answers: AnswersState) => {
  // case num panels edited in summary page
  if (answers.numPanelsExplicit) {
    return answers.numPanelsExplicit;
  }
  // case panels from map
  if (answers.mapDetail && answers.mapDetail.panels.length > 0) {
    return getNumPanels(answers.mapDetail?.panels);
  }
  // case no panels, meaning we skipped
  return 'skipped';
};

const send = (eventObject: any) => {
  window.dataLayer.push(removeUndefined(eventObject));
};

/**
 * tag an answer update (each time the user goes to next step)
 */
const tagUpdateEvent = (route: string, customStep?: number) => {
  const { answers }: { answers: AnswersState } = store.getState();

  const step = answers.businessChoice
    ? getStepIndex(
        {
          flowType: answers.businessChoice,
          flowComplexity: answers.flowChoice,
        },
        route
      )
    : 0;
  const funnelName = parseTaggingName(route);

  // do not send logs if no funnelname (it happens on root redirection)
  if (!funnelName || funnelName === '') return;

  // TODO A verif avec antho, si tout est bon
  const eventObject = {
    event: 'flow_step',
    funnel: isRosselVersion ? 'Simulation Rossel' : 'Simulation',
    funnelStep: customStep ?? step,
    funnelName,

    flowChoice: answers.flowChoice,
    typePanneaux: step >= 1 ? answers.panelId : undefined,
    inclinaison: step >= 2 ? answers.roofAngle : undefined,
    inclinaisonType:
      step >= 2 && answers.roofAngle !== undefined
        ? `${isCustomRoofAngle(answers.roofAngle) ? 'custom' : 'base'}`
        : undefined,
    typeToit: step >= 3 ? answers.roofType : undefined,
    consoAnnuelle:
      step >= 4 ? `${answers.consumption || 'skipped'}` : undefined,
    zipCode:
      answers.flowChoice === 'complex' && step >= 5
        ? answers.addressDetails?.postCode
        : undefined,
    nombrePanneaux:
      answers.flowChoice === 'complex' && step >= 6
        ? getNumPanelsWithExplicit(answers)
        : undefined,
    compteur:
      answers.flowChoice === 'complex' && step >= 7
        ? answers.counter
        : undefined,
    // onduleur:
    //   answers.flowChoice === 'complex' && step >= 8
    //     ? answers.inverter
    //     : undefined,
    batterie:
      (answers.flowChoice === 'complex' && step >= 8) ||
      (answers.flowChoice === 'simple' && step >= 5)
        ? convertBool(answers.needBattery)
        : undefined,
    evehicule:
      (answers.flowChoice === 'complex' && step >= 9) ||
      (answers.flowChoice === 'simple' && step >= 5)
        ? convertBool(answers.needVehicle)
        : undefined,
  };

  send(eventObject);
};

const tagStartEvent = (route: string) => {
  const event = {
    funnel: 'Simulation',
    funnelStep: 0,
    funnelName: parseTaggingName(route),
    event: 'flow_step',
  };
  send(event);
};

const tagSummaryEdit = (editedItem: string) => {
  const event = { event: 'summary_edit', element: editedItem };
  send(event);
};

const tagSendEmail = () => {
  const event = { event: 'sendSimulationEmail' };
  send(event);
};

const tagAskMeeting = () => {
  const event = { event: 'requestMeeting' };
  send(event);
};

const tagging = {
  tagStartEvent,
  tagUpdateEvent,
  tagSummaryEdit,
  tagSendEmail,
  tagAskMeeting,
};

export default tagging;
